import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const GREETING: Message = {
  role: 'assistant',
  content: "Hi! I'm the BunyadKar Assistant. Ask me anything about the program, phases, courses, pitching, or how to get started.",
};

const ChatWidget: React.FC = () => {
  const [open, setOpen]       = useState(false);
  const [messages, setMessages] = useState<Message[]>([GREETING]);
  const [input, setInput]     = useState('');
  const [loading, setLoading] = useState(false);
  const [pulsed, setPulsed]   = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef  = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setPulsed(false), 3000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (open) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => inputRef.current?.focus(), 220);
    }
  }, [open, messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const next: Message[] = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: next }),
      });
      const data = await res.json();
      const reply = data.reply || "Sorry, I couldn't process that. Please try again.";
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Connection error. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <>
      <style>{`
        @keyframes bk-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(91,168,160,0.5); }
          50% { box-shadow: 0 0 0 10px rgba(91,168,160,0); }
        }
        @keyframes bk-panel-in {
          from { opacity: 0; transform: translateY(12px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes bk-dot {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
          40%            { transform: scale(1);   opacity: 1; }
        }
        .bk-pulse { animation: bk-pulse 1.6s ease-in-out infinite; }
        .bk-panel { animation: bk-panel-in 220ms cubic-bezier(0.16,1,0.3,1) forwards; }
        .bk-dot { display: inline-block; width: 6px; height: 6px; border-radius: 50%; background: currentColor; animation: bk-dot 1.2s infinite; }
        .bk-dot:nth-child(2) { animation-delay: 0.2s; }
        .bk-dot:nth-child(3) { animation-delay: 0.4s; }
        .bk-msgs::-webkit-scrollbar { width: 4px; }
        .bk-msgs::-webkit-scrollbar-track { background: transparent; }
        .bk-msgs::-webkit-scrollbar-thumb { background: rgba(100,116,139,0.25); border-radius: 4px; }
      `}</style>

      {/* Chat panel */}
      {open && (
        <div
          className="bk-panel fixed bottom-[88px] right-6 z-50 flex flex-col bg-shell border border-line overflow-hidden"
          style={{ width: 380, height: 520, borderRadius: 20, boxShadow: '0 24px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(255,255,255,0.04)' }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 h-14 border-b border-line flex-shrink-0 bg-shell">
            <img src="/logo.png" alt="BunyadKar" className="h-6 w-auto" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-text leading-none">BunyadKar Assistant</p>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-chart flex-shrink-0" />
              <span className="text-xs text-muted">Online</span>
            </div>
          </div>

          {/* Messages */}
          <div className="bk-msgs flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-2.5">
            {messages.map((m, i) => (
              <div key={i} className={`flex items-end gap-2 ${m.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                {m.role === 'assistant' && (
                  <div
                    className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold"
                    style={{ fontSize: 10, background: '#5BA8A0', marginBottom: 2 }}
                  >B</div>
                )}
                <div
                  className={`max-w-[80%] text-sm leading-relaxed px-3 py-2 ${
                    m.role === 'user'
                      ? 'bg-chart text-white'
                      : 'bg-surface text-text'
                  }`}
                  style={{
                    borderRadius: m.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    wordBreak: 'break-word',
                  }}
                >
                  {m.role === 'user' ? (
                    m.content
                  ) : (
                    <ReactMarkdown
                      components={{
                        p:      ({ children }) => <p className="mb-1.5 last:mb-0">{children}</p>,
                        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                        em:     ({ children }) => <em className="italic">{children}</em>,
                        code:   ({ children }) => <code className="bg-black/10 rounded px-1 font-mono text-xs">{children}</code>,
                        ul:     ({ children }) => <ul className="list-disc pl-4 mb-1.5 space-y-0.5">{children}</ul>,
                        ol:     ({ children }) => <ol className="list-decimal pl-4 mb-1.5 space-y-0.5">{children}</ol>,
                        li:     ({ children }) => <li>{children}</li>,
                        h1:     ({ children }) => <p className="font-semibold mb-1">{children}</p>,
                        h2:     ({ children }) => <p className="font-semibold mb-1">{children}</p>,
                        h3:     ({ children }) => <p className="font-semibold mb-1">{children}</p>,
                        a:      ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="underline opacity-80 hover:opacity-100">{children}</a>,
                      }}
                    >
                      {m.content}
                    </ReactMarkdown>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex items-end gap-2">
                <div
                  className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold"
                  style={{ fontSize: 10, background: '#5BA8A0', marginBottom: 2 }}
                >B</div>
                <div className="bg-surface text-muted px-3 py-2.5 flex gap-1.5" style={{ borderRadius: '16px 16px 16px 4px' }}>
                  <span className="bk-dot" />
                  <span className="bk-dot" />
                  <span className="bk-dot" />
                </div>
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="flex items-center gap-2 px-3 h-14 border-t border-line flex-shrink-0 bg-shell">
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Ask about BunyadKar…"
              disabled={loading}
              className="flex-1 bg-transparent text-sm text-text placeholder:text-muted outline-none disabled:opacity-50"
            />
            <button
              onClick={send}
              disabled={!input.trim() || loading}
              className="w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center text-white transition-opacity disabled:opacity-40"
              style={{ background: '#5BA8A0' }}
              aria-label="Send"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Toggle button */}
      <button
        onClick={() => setOpen(o => !o)}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center text-white transition-transform hover:scale-105 active:scale-95 ${pulsed && !open ? 'bk-pulse' : ''}`}
        style={{ background: '#5BA8A0', boxShadow: '0 4px 20px rgba(91,168,160,0.45)' }}
        aria-label={open ? 'Close chat' : 'Open BunyadKar Assistant'}
      >
        {open ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            <circle cx="9" cy="10" r="1" fill="currentColor" />
            <circle cx="12" cy="10" r="1" fill="currentColor" />
            <circle cx="15" cy="10" r="1" fill="currentColor" />
          </svg>
        )}
      </button>
    </>
  );
};

export default ChatWidget;
